import {
  isFollowUpSourceType,
  type TrainingModuleSchema,
  type TrainingPageDef,
  type TrainingPageType,
  type TrainingSection,
} from '@hcc/types';

/**
 * Pure, immutable update helpers for a training module schema draft.
 * Mirrors the form builder's draftOps: the editor holds the schema in
 * useState and calls these.
 */

export { genId } from './ids';
import { genId } from './ids';
import { makeBlock } from './blocks/blockOps';

export function emptySchema(): TrainingModuleSchema {
  return { pages: [makePage('slide')] };
}

/**
 * Append externally-generated pages (e.g. from the AI builder) to the end of
 * the deck. Any page whose id is missing or collides with an existing page id
 * gets a fresh one, so page ids stay unique (option/block ids are scoped to
 * their page and never collide across pages).
 */
export function appendPages(
  s: TrainingModuleSchema,
  pages: TrainingPageDef[],
): TrainingModuleSchema {
  const seen = new Set(s.pages.map((p) => p.id));
  const added = pages.map((p) => {
    if (!p.id || seen.has(p.id)) {
      const id = genId('page');
      seen.add(id);
      return { ...p, id };
    }
    seen.add(p.id);
    return p;
  });
  return { ...s, pages: [...s.pages, ...added] };
}

export function makePage(type: TrainingPageType): TrainingPageDef {
  switch (type) {
    case 'slide':
      // New slides are block-based; legacy body slides remain editable.
      return { id: genId('slide'), type: 'slide', title: '', blocks: [makeBlock('text')] };
    case 'question_group': {
      // Seeded No / Yes, the overwhelmingly common assessment grid; both the
      // columns and the rows are editable from there.
      const no = genId('opt');
      const yes = genId('opt');
      return {
        id: genId('grp'),
        type: 'question_group',
        title: '',
        options: [
          { id: no, label: 'No' },
          { id: yes, label: 'Yes' },
        ],
        rows: [{ id: genId('q'), prompt: '', points: 10 }],
        image_position: 'below',
        image_align: 'center',
        image_size: 'medium',
      };
    }
    case 'text_group':
      // Unmarked-only, so rows carry no weight: points stay 0.
      return {
        id: genId('txt'),
        type: 'text_group',
        title: '',
        rows: [{ id: genId('q'), prompt: '', points: 0 }],
      };
    case 'follow_up':
      return {
        id: genId('fup'),
        type: 'follow_up',
        title: '',
        match_label: 'Yes',
        multiline: true,
      };
    case 'single_choice':
      return {
        id: genId('q'),
        type: 'single_choice',
        prompt: '',
        points: 10,
        options: [
          { id: genId('opt'), label: '' },
          { id: genId('opt'), label: '' },
        ],
        correct_option_id: '',
      };
    case 'multi_select':
      return {
        id: genId('q'),
        type: 'multi_select',
        prompt: '',
        points: 10,
        options: [
          { id: genId('opt'), label: '' },
          { id: genId('opt'), label: '' },
        ],
        correct_option_ids: [],
      };
    case 'true_false':
      return {
        id: genId('q'),
        type: 'true_false',
        prompt: '',
        points: 10,
        correct: true,
      };
    case 'short_text':
      return {
        id: genId('q'),
        type: 'short_text',
        prompt: '',
        points: 10,
        accepted: [''],
      };
    case 'categorize': {
      const catA = genId('cat');
      const catB = genId('cat');
      return {
        id: genId('q'),
        type: 'categorize',
        prompt: '',
        points: 10,
        categories: [
          { id: catA, label: '' },
          { id: catB, label: '' },
        ],
        items: [{ id: genId('item'), label: '', category_id: catA }],
      };
    }
    case 'ordering':
      return {
        id: genId('q'),
        type: 'ordering',
        prompt: '',
        points: 10,
        items: [
          { id: genId('item'), label: '' },
          { id: genId('item'), label: '' },
        ],
      };
    case 'hotspot':
      return {
        id: genId('q'),
        type: 'hotspot',
        prompt: '',
        points: 10,
        image_key: '',
        regions: [],
      };
    case 'matching':
      return {
        id: genId('q'),
        type: 'matching',
        prompt: '',
        points: 10,
        pairs: [
          { id: genId('pair'), left: '', right: '' },
          { id: genId('pair'), left: '', right: '' },
        ],
      };
  }
}

export function addPage(s: TrainingModuleSchema, type: TrainingPageType): TrainingModuleSchema {
  return { ...s, pages: [...s.pages, makePage(type)] };
}

/** Preset slide arrangements for the common "set piece" pages. */
export const SLIDE_TEMPLATES = ['title', 'section', 'statement'] as const;
export type SlideTemplate = (typeof SLIDE_TEMPLATES)[number];

export const SLIDE_TEMPLATE_LABEL: Record<SlideTemplate, string> = {
  title: 'Title slide',
  section: 'Section break',
  statement: 'Big statement',
};

export function makeTemplateSlide(template: SlideTemplate): TrainingPageDef {
  const id = genId('slide');
  switch (template) {
    case 'title':
      return {
        id,
        type: 'slide',
        title: '',
        blocks: [
          { id: genId('blk'), type: 'statement', text: 'Welcome to this training module' },
          {
            id: genId('blk'),
            type: 'text',
            text: 'Work through the slides and answer every question. Click Next to begin.',
          },
        ],
      };
    case 'section':
      return {
        id,
        type: 'slide',
        title: '',
        blocks: [
          { id: genId('blk'), type: 'divider' },
          { id: genId('blk'), type: 'statement', text: 'Section title' },
          { id: genId('blk'), type: 'divider' },
        ],
      };
    case 'statement':
      return {
        id,
        type: 'slide',
        title: '',
        blocks: [{ id: genId('blk'), type: 'statement', text: 'The key message goes here' }],
      };
  }
}

export function addTemplateSlide(
  s: TrainingModuleSchema,
  template: SlideTemplate,
): TrainingModuleSchema {
  return { ...s, pages: [...s.pages, makeTemplateSlide(template)] };
}

/**
 * One slide per uploaded picture, each a single full-width image block.
 * The fast path for migrating an existing deck: screenshot every page,
 * select them all, then author just the quiz. Files arrive in selection
 * order and become pages in that order.
 */
export function addPictureSlides(
  s: TrainingModuleSchema,
  urls: readonly string[],
): TrainingModuleSchema {
  const pages = urls.map(
    (url): TrainingPageDef => ({
      id: genId('slide'),
      type: 'slide',
      title: '',
      blocks: [{ id: genId('blk'), type: 'image', image_key: url, size: 'full' }],
    }),
  );
  return { ...s, pages: [...s.pages, ...pages] };
}

/**
 * Insert a deep copy of the page right after the original. Only the page
 * id must be regenerated (the validator requires page ids to be unique;
 * option / pair / block ids only need to be unique within their page, so
 * keeping them avoids remapping answer-key references).
 */
export function duplicatePage(s: TrainingModuleSchema, index: number): TrainingModuleSchema {
  const source = s.pages[index];
  if (!source) return s;
  const copy = { ...(JSON.parse(JSON.stringify(source)) as TrainingPageDef), id: genId('copy') };
  const pages = [...s.pages];
  pages.splice(index + 1, 0, copy);
  return { ...s, pages };
}

/**
 * The choice pages before `index` that a follow-up page there may look back
 * over, labelled for the source picker. Grids are listed by title, single
 * questions by prompt.
 */
export function followUpSources(
  pages: TrainingPageDef[],
  index: number,
): { id: string; label: string }[] {
  return pages.slice(0, index).flatMap((p, i) => {
    if (!isFollowUpSourceType(p.type)) return [];
    const text =
      p.type === 'question_group'
        ? p.title || `Question grid (${p.rows.length} rows)`
        : 'prompt' in p
          ? p.prompt || 'Untitled question'
          : '';
    return [{ id: p.id, label: `Page ${i + 1}: ${text}` }];
  });
}

export function removePage(s: TrainingModuleSchema, index: number): TrainingModuleSchema {
  return { ...s, pages: s.pages.filter((_, i) => i !== index) };
}

export function movePage(
  s: TrainingModuleSchema,
  index: number,
  dir: -1 | 1,
): TrainingModuleSchema {
  const to = index + dir;
  if (to < 0 || to >= s.pages.length) return s;
  const pages = [...s.pages];
  const [page] = pages.splice(index, 1);
  pages.splice(to, 0, page!);
  return { ...s, pages };
}

/** Move a page from one index to another (drag reorder). */
export function reorderPages(
  s: TrainingModuleSchema,
  from: number,
  to: number,
): TrainingModuleSchema {
  if (from === to || from < 0 || to < 0 || from >= s.pages.length || to >= s.pages.length) return s;
  const pages = [...s.pages];
  const [page] = pages.splice(from, 1);
  pages.splice(to, 0, page!);
  return { ...s, pages };
}

/**
 * Where an index lands after the element at `from` is moved to `to`. Used to
 * keep the selected page selected as a drag reorders the list under it.
 */
export function remapIndexAfterMove(active: number, from: number, to: number): number {
  if (active === from) return to;
  if (from < active && active <= to) return active - 1;
  if (to <= active && active < from) return active + 1;
  return active;
}

/** Replace the page at index with an updated copy (same id and type). */
export function updatePage(
  s: TrainingModuleSchema,
  index: number,
  next: TrainingPageDef,
): TrainingModuleSchema {
  return { ...s, pages: s.pages.map((p, i) => (i === index ? next : p)) };
}

/* -------------------------------------------------------------------------
 * Sections (pass-gated / grouping bands over the page list)
 * -----------------------------------------------------------------------*/

export function addSection(s: TrainingModuleSchema): TrainingModuleSchema {
  const section: TrainingSection = { id: genId('sec'), title: '' };
  return { ...s, sections: [...(s.sections ?? []), section] };
}

export function updateSection(
  s: TrainingModuleSchema,
  id: string,
  patch: Partial<Omit<TrainingSection, 'id'>>,
): TrainingModuleSchema {
  return {
    ...s,
    sections: (s.sections ?? []).map((sec) => (sec.id === id ? { ...sec, ...patch } : sec)),
  };
}

/** Remove a section and clear it from any page that referenced it. */
export function removeSection(s: TrainingModuleSchema, id: string): TrainingModuleSchema {
  return {
    ...s,
    sections: (s.sections ?? []).filter((sec) => sec.id !== id),
    pages: s.pages.map((p) => (p.section_id === id ? stripSection(p) : p)),
  };
}

/** Assign (or clear, with null) a page's section. */
export function setPageSection(
  s: TrainingModuleSchema,
  index: number,
  sectionId: string | null,
): TrainingModuleSchema {
  return {
    ...s,
    pages: s.pages.map((p, i) =>
      i === index ? (sectionId ? { ...p, section_id: sectionId } : stripSection(p)) : p,
    ),
  };
}

function stripSection(page: TrainingPageDef): TrainingPageDef {
  const { section_id: _omit, ...rest } = page;
  void _omit;
  return rest as TrainingPageDef;
}
